import AppLayout from "@/layout/AppLayout.vue"
import { createRouter, createWebHistory } from "vue-router"
import { permissionsService } from "@/services/PermissionsService"
import { useAuthenticationStore } from "@/store"

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      component: AppLayout,
      children: [
        {
          path: "/",
          name: "dashboard",
          component: () => import("@/views/Dashboard.vue")
        },
        {
          path: "/manage-documents",
          name: "manage-documents",
          component: () => import("@/views/ManageDocuments.vue")
        },
        {
          path: "/document-types",
          name: "document-types",
          component: () => import("@/views/DocumentTypes.vue"),
          meta: {
            permission: "platform.systems.document_types"
          }
        },
        {
          path: "/document-types/new",
          name: "document-type-create",
          component: () => import("@/views/DocumentTypeWizard.vue"),
          meta: {
            permission: "platform.systems.document_types"
          }
        },
        {
          path: "/document-types/:id/edit",
          name: "document-type-edit",
          component: () => import("@/views/DocumentTypeWizard.vue"),
          props: true,
          meta: {
            permission: "platform.systems.document_types"
          }
        },
        {
          path: "/file-manager",
          name: "file-manager",
          component: () => import("@/views/FileManager.vue")
        },
        {
          path: "/settings",
          name: "settings",
          component: () => import("@/views/Settings.vue")
        },
        {
          path: "/document-analytics/:documentId",
          name: "document-analytics",
          component: () => import("@/views/DocumentAnalytic.vue"),
          meta: {
            permission: "platform.systems.document_analytics"
          }
        },
        {
          path: "/nomenclatures",
          name: "nomenclatures",
          component: () => import("@/views/Nomenclatures.vue")
        },
        {
          path: "/create-list",
          redirect: "/nomenclatures"
        },
        {
          path: "/connectors",
          name: "connectors",
          component: () => import("@/views/ConnectorCatalog.vue")
        },
        {
          path: "/connectors/:connectorId/setup",
          name: "connector-setup",
          component: () => import("@/views/ConnectorSetup.vue"),
          props: true
        },
        {
          path: "/connectors/:id/account-mapping",
          name: "connector-account-mapping",
          component: () => import("@/views/ConnectorAccountMapping.vue")
        },
        {
          path: "/accounts",
          name: "accounts",
          component: () => import("@/views/AccountManagement.vue")
        },
        {
          path: "/documents/:id/processing",
          name: "document-processing",
          component: () => import("@/views/DocumentProcessing.vue")
        }
      ]
    },
    {
      path: "/:pathMatch(.*)*",
      name: "not-found",
      component: () => import("@/views/pages/NotFound.vue")
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/pages/Login.vue"),
      beforeEnter: (to, from, next) => {
        const authStore = useAuthenticationStore()
        if (authStore.requiresTwoFactor || !authStore.isLoggedIn) {
          return next()
        }
        return next({ path: "/" })
      }
    },
    {
      path: "/verify-user",
      name: "verify-user",
      component: () => import("@/views/pages/VerifyUser.vue")
    },
    {
      path: "/forgotten-password",
      name: "forgotten-password",
      component: () => import("@/views/pages/ForgottenPassword.vue"),
      beforeEnter: (to, from, next) => {
        return useAuthenticationStore().isLoggedIn
          ? next({ path: "/" })
          : next()
      }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  if (to.name !== "verify-user") {
    await useAuthenticationStore().verifySession()
  }

  const authStore = useAuthenticationStore()
  const publicRoutes = ["login", "verify-user", "forgotten-password"]

  if (authStore.requiresTwoFactor && to.name !== "login") {
    return next({ path: "/login" })
  }

  if (!authStore.isLoggedIn && !publicRoutes.includes(to.name)) {
    return next({ path: "/login" })
  }

  const requiredPermission = to.meta.permission

  if (
    requiredPermission &&
    !permissionsService.hasPermission(requiredPermission)
  ) {
    return next({ path: "/" })
  }

  next()
})

export default router
